// « Départs » — the departures sheet for the desk
// (PMS-TERMINAL-LINK-BRIEF §8.8 / §8.8g / §8.8h).
//
// Read after the paper « sur ma chambre » tickets are posted, printed, and used
// at checkout. Two sections since 2026-09-25 (operator): on top, the bookings
// with something still to collect; below, the ones already settled, each with
// every payment — date, method, amount — and the date of the last one, because
// HOW a guest paid (card or not) is part of the checks.
//
// Each row still to collect can open the Encaissement form prefilled with the
// room and what is left — « 5 En attente » included, because the form is the
// same one.
//
// Interim source: the Vega mirror plus the Encaissement log. For a guest still
// in the house the note is Vega's bill at checkout: the running bill's lines
// not yet invoiced (restaurant and bar extras included) plus the FACT AUTO
// plan's nights not yet posted, less Vega's arrhes. Once the guest has LEFT
// (§8.8h) it is Vega's pending notes, and a line still live on the bill with no
// note at all — posted, never invoiced (§8.8i) — is listed « non facturé · à
// vérifier », never counted. A line the desk DELETED (a dinner inside the
// demi-pension, a breakfast not taken) is on neither. At go-live the adapter
// changes, not this screen.
//
// §8.8k (operator 2026-09-26): a payment taken in the form carries a red ⚠
// until the feed reads it in Vega; the sheet re-reads every minute so the mark
// clears on its own; and an open « en attente » is settled or cancelled from
// the row that carries it — the retired Journal's job.
//
// §8.8l (operator 2026-09-26): a third section, « Clients débiteurs » — a guest
// who left with notes put on a debtor account in Vega (« Transfert débiteur »).
// Vega then marks those notes settled, so without it CADAY read « soldé » on
// the morning it left owing 1 135,38.
//
// §8.8o (operator 2026-09-26/27): the payments leave the guest's cell, one
// line each, never wrapped, split into « Réglé avant » (before the sheet's
// day: MARWIN's card payment at his check-in on 24/09 is not 25/09's money)
// and « Réglé le JJ/MM » — the check-out shift's takings, totalled by tender
// on top (DayTakings). A payment made after the day is listed « plus tard »,
// never counted there. Six rooms or more read as a count and by floor.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FindingRow } from './CardFindingsContext.js';
import type { ReactNode } from 'react';
import type {
  DepartureBill,
  DepartureBriefing,
  IsoDate,
  OtherTaking,
  PaymentLine,
  ReceptionPaymentId,
  TakingsBriefing,
  UnbilledLine,
} from '@lemontaigu/pms-core';
import { fmtAmount, fmtDate } from './logic.js';
import {
  billTarget,
  dayMonth,
  defaultDepartureDate,
  displayName,
  displayRoom,
  fmtQty,
  paidCents,
  paymentStamp,
  sortDepartures,
  othersTakings,
  pageTakings,
  splitByDay,
  splitBySettlement,
  todayParis,
  toCollectCents,
  transferredCents,
  vegaReadLabel,
  clientTag,
  type DepartureSort,
  type SuspenseToSettle,
} from './departureSheet.js';
import { NotInVega, PaymentList } from './PaymentList.js';
import { OtherTakings } from './OtherTakings.js';
import { DayBar } from './DayBar.js';
import { DayTakings, type LitBucket } from './DayTakings.js';
import { RoomList } from './RoomList.js';
import { SheetTop } from './SheetTop.js';
import { SortChips, type SortOption } from './SortChips.js';
import { SuspenseActions } from './SuspenseActions.js';
import { SHEET_REFRESH_MS, useAutoRefresh } from './useAutoRefresh.js';
import { EtablissementTag } from './EtablissementTag.js';

const SORTS: SortOption<DepartureSort>[] = [
  { key: 'room', label: 'Chambre' },
  { key: 'name', label: 'Nom' },
  { key: 'paid', label: 'Heure de règlement' },
];

export interface DeparturesProps {
  briefing: DepartureBriefing;
  /** The day's « Autres encaissements » (§8.8p); none without it. */
  takings?: TakingsBriefing;
  /** The view chips, placed at the top by the Encaissement screen (§8.8k). */
  nav?: ReactNode;
  /** The Encaissement form, placed under the sheet's title. */
  form?: ReactNode;
  /** Open the Encaissement form on this room, with this amount prefilled. */
  onBill: (room: string, amountCents: number) => void;
  canBill: boolean;
  /** « Régler » / « Annuler » of an open « en attente » (§8.8k). */
  onSettle?: (s: SuspenseToSettle) => void;
  onCancelSuspense?: (id: ReceptionPaymentId) => void;
  /** Bumped by the form after each payment it records — the sheet re-reads. */
  refreshKey?: number;
  /** How often the sheet re-reads itself while on screen; 0 turns it off. */
  refreshMs?: number;
  /** Injected for tests; the wall clock otherwise. */
  now?: Date;
}

/** A row's open « en attente », as the form needs them to settle one. */
const suspensesOf = (b: DepartureBill): SuspenseToSettle[] =>
  b.suspenses.map((s) => ({ id: s.id, cents: s.cents, target: s.roomNumber ?? b.bookingRef }));

function Rooms({ b }: { b: DepartureBill }) {
  return (
    <>
      <RoomList rooms={b.rooms} />
      {b.unreleased && (
        <span className="pms-sub" title="Départ coupé à l'arrivée suivante : chambre non libérée dans Vega">
          {' '}
          ⚠ non libérée
        </span>
      )}
    </>
  );
}

/** Who the row is for, and on which account (§8.8j): a group's own account
 *  reads « groupe », a room's account its guest and the group it belongs to —
 *  named once: the leader's room often carries the group's own name (CADAY's
 *  214). The booking's client number and résa follow the name that owns them
 *  (operator 2026-09-26: « FORMATION (033276) - 024608 »). */
function Client({ b }: { b: DepartureBill }) {
  const name = displayName(b);
  const room = b.unit && b.unit !== 'GRP' ? b.unit : null;
  const tag = clientTag(b.clientRef, b.bookingRef);
  return (
    <>
      {name}
      <span className="pms-sub">
        {room && name !== b.partyLabel
          ? ` · extras ch. ${displayRoom(room)} · ${b.partyLabel} ${tag}`
          : ` ${tag}${room ? ` · extras ch. ${displayRoom(room)}` : ''}`}
        {b.unit === 'GRP' && ' · groupe (hébergement)'}
        {b.checkedOut && ' · parti'}
      </span>
      <EtablissementTag code={b.propertyCode} />
    </>
  );
}

const rowKey = (b: DepartureBill) => `${b.bookingRef}-${b.unit ?? ''}`;

/** « Réglé avant » and « Réglé le JJ/MM » — two cells, each its total over its
 *  lines; a payment made after the day under the second, « plus tard », not
 *  in its total (the sheet's money is Vega's live state, so it stays in view). */
function PaidCells({ payments, day, bold = false }: { payments: PaymentLine[]; day: IsoDate; bold?: boolean }) {
  const s = splitByDay(payments, day);
  const before = paidCents(s.before);
  const on = paidCents(s.onDay);
  const amount = (list: PaymentLine[], cents: number) =>
    list.length === 0 ? '—' : bold ? <strong>{fmtAmount(cents)}</strong> : fmtAmount(cents);
  return (
    <>
      <td className="pms-num">
        {amount(s.before, before)}
        <PaymentList payments={s.before} day={day} total={before} />
      </td>
      <td className="pms-num">
        {amount(s.onDay, on)}
        <PaymentList payments={s.onDay} day={day} total={on} />
        {s.after.length > 0 && (
          <div className="pms-later" title="Réglé après ce jour-là : compté le jour du règlement">
            <span className="pms-sub">plus tard</span>
            <PaymentList payments={s.after} day={day} />
          </div>
        )}
      </td>
    </>
  );
}

/** The last payment's stamp, time included when known (« 25/09 09:17 »). */
function lastPaymentStamp(b: DepartureBill): string {
  if (b.payments.length === 0) return '—';
  const last = [...b.payments].sort((x, y) =>
    (x.at ?? x.date ?? '') < (y.at ?? y.date ?? '') ? -1 : (x.at ?? x.date ?? '') > (y.at ?? y.date ?? '') ? 1 : 0,
  );
  return paymentStamp(last[last.length - 1]);
}

/** Lines still live on a departed guest's bill, on no note — shown, never counted. */
function Unbilled({ lines }: { lines: UnbilledLine[] }) {
  if (lines.length === 0) return null;
  return (
    <div
      className="pms-unbilled"
      title="Lignes encore sur la note Vega, jamais facturées : la facture reste à faire dans Vega (voir « Non facturés »)."
    >
      <span className="pms-sub">non facturé · à vérifier</span>
      <ul className="pms-pay-list">
        {lines.map((l, i) => (
          <li key={i}>
            {l.label ?? '?'} ·{' '}
            {l.qty != null && l.unitPriceCents != null ? `${fmtQty(l.qty)} × ${fmtAmount(l.unitPriceCents)} = ` : ''}
            {fmtAmount(l.cents)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Departures({
  briefing,
  takings,
  nav,
  form,
  onBill,
  canBill,
  onSettle,
  onCancelSuspense,
  refreshKey = 0,
  refreshMs = SHEET_REFRESH_MS,
  now,
}: DeparturesProps) {
  const [date, setDate] = useState<IsoDate>(() => defaultDepartureDate(now ?? new Date()));
  const [rows, setRows] = useState<DepartureBill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lit, setLit] = useState<LitBucket>(null);
  const [others, setOthers] = useState<OtherTaking[]>([]);
  const clock = now ?? new Date();
  // Operator 2026-09-25: by room to collect, by the time they paid once paid.
  const [dueSort, setDueSort] = useState<DepartureSort>('room');
  const [paidSort, setPaidSort] = useState<DepartureSort>('paid');
  const [debtSort, setDebtSort] = useState<DepartureSort>('room');
  // Only the latest read may land: the minute tick races the date bar.
  const latest = useRef(0);

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++latest.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      const [r, o] = await Promise.all([
        briefing.departures(date),
        takings ? takings.dayOthers(date) : Promise.resolve(null),
      ]);
      if (mine !== latest.current) return;
      setLoading(false);
      if (o?.ok) setOthers(o.value.filter((x) => x.shift === 'checkout'));
      else if (!quiet) setOthers([]);
      if (!r.ok) {
        setError(r.error.message ?? 'lecture impossible');
        if (!quiet) setRows([]);
        return;
      }
      setError(null);
      setRows(r.value);
    },
    [briefing, takings, date],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // After the form records a payment: re-read quietly (no « Chargement… »).
  const seenKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === seenKey.current) return;
    seenKey.current = refreshKey;
    void load(true);
  }, [refreshKey, load]);

  const quietLoad = useCallback(() => void load(true), [load]);
  useAutoRefresh(quietLoad, refreshMs);

  const split = useMemo(() => splitBySettlement(rows), [rows]);
  const toCollect = useMemo(() => sortDepartures(split.toCollect, dueSort), [split, dueSort]);
  const settled = useMemo(() => sortDepartures(split.settled, paidSort), [split, paidSort]);
  const debtors = useMemo(() => sortDepartures(split.debtors, debtSort), [split, debtSort]);

  const totals = useMemo(
    () =>
      toCollect.reduce(
        (t, b) => ({
          bill: t.bill + b.billCents,
          deposit: t.deposit + (b.checkedOut ? 0 : b.depositCents),
          collected: t.collected + b.collectedCents,
          pending: t.pending + b.pendingCents,
          toCollect: t.toCollect + toCollectCents(b),
        }),
        { bill: 0, deposit: 0, collected: 0, pending: 0, toCollect: 0 },
      ),
    [toCollect],
  );
  // Each section's « Réglé avant » and « Réglé le JJ/MM » totals.
  const paidSplit = (list: DepartureBill[]) =>
    list.reduce(
      (t, b) => {
        const s = splitByDay(b.payments, date);
        return { before: t.before + paidCents(s.before), on: t.on + paidCents(s.onDay) };
      },
      { before: 0, on: 0 },
    );
  const collectPaid = paidSplit(toCollect);
  const settledPaid = paidSplit(settled);
  const debtorPaid = paidSplit(debtors);
  // The check-out shift's takings: what the day's departures paid that day,
  // and its « Autres » (§8.8p).
  const till = useMemo(
    () => [...pageTakings(rows.flatMap((b) => splitByDay(b.payments, date).onDay)), ...othersTakings(others, 'checkout')],
    [rows, others, date],
  );
  const dm = dayMonth(date);
  const debtorTotals = useMemo(
    () =>
      debtors.reduce(
        (t, b) => ({
          transferred: t.transferred + transferredCents(b),
          owed: t.owed + b.debtCents,
        }),
        { transferred: 0, owed: 0 },
      ),
    [debtors],
  );

  const readAt = rows.find((b) => b.readAt)?.readAt ?? null;
  const read = vegaReadLabel(readAt, clock);
  const plural = (n: number) => (n > 1 ? 's' : '');

  return (
    <section className="pms-print" aria-label="Départs" data-lit={lit ?? undefined}>
      <SheetTop nav={nav} onRefresh={() => void load()} canPrint={rows.length > 0} />
      <DayBar value={date} onChange={setDate} today={todayParis(clock)} label="Date de départ" />
      <div className="pms-head">
        <div>
          <h2>Départs du {fmtDate(date)}</h2>
          <p className="pms-hint">
            {read && (
              <span className={read.stale ? 'pms-stale' : undefined}>
                {read.stale ? '⚠ ' : ''}
                {read.text}
                {' · '}
              </span>
            )}
            Note Vega à régler : lignes non encore facturées et nuits restantes · arrhes Vega déduites · client parti :
            notes Vega en attente · groupe : une ligne pour le groupe, une par chambre pour ses extras ·{' '}
            <NotInVega /> pris au formulaire, pas encore saisi dans Vega · client débiteur : notes mises en compte
            dans Vega (transfert débiteur) · en gras : règlement hors carte.
          </p>
        </div>
      </div>

      {date <= todayParis(clock) && (
        <DayTakings title={`Encaissé aux départs du ${dm}`} lines={till} lit={lit} onLight={setLit} />
      )}
      {form && <div className="pms-no-print pms-sheet-form">{form}</div>}
      {error && <p className="pms-warn">⚠ {error}</p>}
      {loading && <p className="pms-hint pms-no-print">Chargement…</p>}

      <div className="pms-section-bar">
        <h3 className="pms-section-title">
          À encaisser <span className="pms-sub">({toCollect.length})</span>
        </h3>
        <SortChips section="À encaisser" value={dueSort} options={SORTS} onChange={setDueSort} />
      </div>
      <div className="pms-table-wrap">
        <table className="pms-table" aria-label="À encaisser">
          <thead>
            <tr>
              <th>Chambre(s)</th>
              <th>Client</th>
              <th title={`Réglé avant le ${dm}`}>Réglé avant</th>
              <th>Réglé le {dm}</th>
              <th>Note</th>
              <th>Arrhes</th>
              <th>Encaissé</th>
              <th>En attente</th>
              <th>À encaisser</th>
            </tr>
          </thead>
          <tbody>
            {toCollect.map((b) => {
              const due = toCollectCents(b);
              const target = billTarget(b);
              return (
                <Fragment key={rowKey(b)}>
                <tr>
                  <td>
                    <Rooms b={b} />
                  </td>
                  <td>
                    <Client b={b} />
                    <Unbilled lines={b.unbilledLines} />
                  </td>
                  <PaidCells payments={b.payments} day={date} />
                  <td className="pms-num">
                    {fmtAmount(b.billCents)}
                    {b.checkedOut
                      ? b.pendingNotes.length > 0 && (
                          <div className="pms-sub" title="Notes Vega encore en attente de règlement">
                            note{plural(b.pendingNotes.length)} en attente {b.pendingNotes.map((n) => n.facture).join(', ')}
                          </div>
                        )
                      : b.folioLineCount === 0 && <span className="pms-sub"> (aucune ligne)</span>}
                  </td>
                  <td className="pms-num">
                    {b.checkedOut ? (
                      b.depositCents ? (
                        <span
                          className="pms-sub"
                          title="Client parti avec des arrhes encore détenues par Vega : non déduites, à vérifier dans Vega"
                        >
                          {fmtAmount(b.depositCents)} détenues · vérifier
                        </span>
                      ) : (
                        '—'
                      )
                    ) : b.depositCents ? (
                      fmtAmount(b.depositCents)
                    ) : (
                      '—'
                    )}
                    {b.depositUnattributedCents !== 0 && (
                      <span
                        className="pms-sub"
                        title="Arrhes d'un client ayant plusieurs réservations : non déduites, à vérifier dans Vega"
                      >
                        {' '}
                        + {fmtAmount(b.depositUnattributedCents)} à vérifier
                      </span>
                    )}
                  </td>
                  <td className="pms-num">{b.collectedCents ? fmtAmount(b.collectedCents) : '—'}</td>
                  <td className="pms-num">
                    {b.pendingCents ? fmtAmount(b.pendingCents) : '—'}
                    <SuspenseActions
                      items={suspensesOf(b)}
                      canBill={canBill}
                      onSettle={onSettle}
                      onCancel={onCancelSuspense}
                    />
                  </td>
                  <td className="pms-num">
                    <strong>{fmtAmount(due)}</strong>
                    {/* Under the amount, not in a column of its own, so it
                        never falls off the page (operator 2026-09-27). */}
                    {target && canBill && (
                      <div className="pms-cell-action pms-no-print">
                        <button
                          type="button"
                          className="pms-mini"
                          onClick={() => onBill(target, due)}
                          aria-label={
                            b.unit === 'GRP' ? `Encaisser le groupe, résa ${target}` : `Encaisser la chambre ${target}`
                          }
                        >
                          Encaisser
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                <FindingRow clientRef={b.clientRef} bookingRef={b.bookingRef} colSpan={9} />
                </Fragment>
              );
            })}
            {toCollect.length === 0 && !loading && !error && (
              <tr>
                <td colSpan={9} className="pms-empty">
                  {rows.length === 0 ? 'Aucun départ ce jour-là.' : 'Rien à encaisser.'}
                </td>
              </tr>
            )}
          </tbody>
          {toCollect.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={2} className="pms-sub">
                  {toCollect.length} départ{plural(toCollect.length)} à encaisser
                </td>
                <td className="pms-num">{fmtAmount(collectPaid.before)}</td>
                <td className="pms-num">{fmtAmount(collectPaid.on)}</td>
                <td className="pms-num">{fmtAmount(totals.bill)}</td>
                <td className="pms-num">{fmtAmount(totals.deposit)}</td>
                <td className="pms-num">{fmtAmount(totals.collected)}</td>
                <td className="pms-num">{fmtAmount(totals.pending)}</td>
                <td className="pms-num">{fmtAmount(totals.toCollect)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="pms-section-bar">
        <h3 className="pms-section-title">
          Réglés <span className="pms-sub">({settled.length})</span>
        </h3>
        <SortChips section="Réglés" value={paidSort} options={SORTS} onChange={setPaidSort} />
      </div>
      <div className="pms-table-wrap">
        <table className="pms-table" aria-label="Réglés">
          <thead>
            <tr>
              <th>Chambre(s)</th>
              <th>Client</th>
              <th title={`Réglé avant le ${dm}`}>Réglé avant</th>
              <th>Réglé le {dm}</th>
              <th>Dernier règlement</th>
              <th>État</th>
            </tr>
          </thead>
          <tbody>
            {settled.map((b) => {
              const due = toCollectCents(b);
              return (
                <Fragment key={rowKey(b)}>
                <tr>
                  <td>
                    <Rooms b={b} />
                  </td>
                  <td>
                    <Client b={b} />
                  </td>
                  {b.payments.length === 0 ? (
                    <td colSpan={2} className="pms-sub">
                      aucun règlement lu
                    </td>
                  ) : (
                    <PaidCells payments={b.payments} day={date} bold />
                  )}
                  <td>{lastPaymentStamp(b)}</td>
                  <td>
                    {/* Negative is NOT labelled « trop-perçu » (2026-09-25). It is a
                        fact to check, not a refund to make: on the first live day
                        it was THOMAS's 2,00 — 115,32 taken at check-in for a
                        113,32 stay, the TPE-vs-bill slip the PMS must prevent. */}
                    {due === 0 ? (
                      'soldé'
                    ) : (
                      <span
                        className="pms-sub"
                        title="Déjà payé plus que le séjour prévu : montant saisi au TPE différent de la note, séjour modifié, ou trop-perçu réel — vérifier dans Vega"
                      >
                        payé &gt; note de {fmtAmount(-due)} · vérifier
                      </span>
                    )}
                    {b.pendingCents > 0 && (
                      <div className="pms-sub" title="Mise en attente encore ouverte : la régler ou l'annuler">
                        en attente {fmtAmount(b.pendingCents)}
                      </div>
                    )}
                    <SuspenseActions
                      items={suspensesOf(b)}
                      canBill={canBill}
                      onSettle={onSettle}
                      onCancel={onCancelSuspense}
                    />
                    <Unbilled lines={b.unbilledLines} />
                  </td>
                </tr>
                <FindingRow clientRef={b.clientRef} bookingRef={b.bookingRef} colSpan={6} />
                </Fragment>
              );
            })}
            {settled.length === 0 && !loading && !error && (
              <tr>
                <td colSpan={6} className="pms-empty">
                  Aucun départ réglé.
                </td>
              </tr>
            )}
          </tbody>
          {settled.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={2} className="pms-sub">
                  {settled.length} départ{plural(settled.length)} réglé{plural(settled.length)}
                </td>
                <td className="pms-num">{fmtAmount(settledPaid.before)}</td>
                <td className="pms-num">{fmtAmount(settledPaid.on)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* §8.8l (operator 2026-09-26): left with notes put on a debtor account —
          nothing to collect at the desk, and not paid either. */}
      <div className="pms-section-bar">
        <h3 className="pms-section-title">
          Clients débiteurs <span className="pms-sub">({debtors.length})</span>
        </h3>
        <SortChips section="Clients débiteurs" value={debtSort} options={SORTS} onChange={setDebtSort} />
      </div>
      <div className="pms-table-wrap">
        <table className="pms-table" aria-label="Clients débiteurs">
          <thead>
            <tr>
              <th>Chambre(s)</th>
              <th>Client</th>
              <th title={`Réglé avant le ${dm}`}>Réglé avant</th>
              <th>Réglé le {dm}</th>
              <th>Mis en compte</th>
              <th>Reste dû</th>
            </tr>
          </thead>
          <tbody>
            {debtors.map((b) => {
              const names = [...new Set(b.debts.map((d) => d.debtor).filter((x): x is string => !!x))];
              return (
                <tr key={rowKey(b)}>
                  <td>
                    <Rooms b={b} />
                  </td>
                  <td>
                    <Client b={b} />
                  </td>
                  <PaidCells payments={b.payments} day={date} />
                  <td className="pms-num">
                    {fmtAmount(transferredCents(b))}
                    {names.length > 0 && <div className="pms-sub">compte {names.join(', ')}</div>}
                    <ul className="pms-pay-list">
                      {b.debts.map((d) => (
                        <li key={d.facture}>
                          note {d.facture}
                          {d.account ? ` (${d.account === 'GRP' ? 'groupe' : `ch. ${displayRoom(d.account)}`})` : ''} ·{' '}
                          {fmtAmount(d.cents)}
                          {d.recoveredCents !== 0 && ` · recouvré ${fmtAmount(d.recoveredCents)}`}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="pms-num">
                    {b.debtCents !== 0 ? <strong>{fmtAmount(b.debtCents)}</strong> : 'recouvré'}
                  </td>
                </tr>
              );
            })}
            {debtors.length === 0 && !loading && !error && (
              <tr>
                <td colSpan={6} className="pms-empty">
                  Aucun départ mis en compte débiteur.
                </td>
              </tr>
            )}
          </tbody>
          {debtors.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={2} className="pms-sub">
                  {debtors.length} départ{plural(debtors.length)} mis en compte
                </td>
                <td className="pms-num">{fmtAmount(debtorPaid.before)}</td>
                <td className="pms-num">{fmtAmount(debtorPaid.on)}</td>
                <td className="pms-num">{fmtAmount(debtorTotals.transferred)}</td>
                <td className="pms-num">{fmtAmount(debtorTotals.owed)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {takings && (
        <>
          <div className="pms-section-bar">
            <h3 className="pms-section-title">
              Autres encaissements du jour <span className="pms-sub">({others.length}) · service départs</span>
            </h3>
          </div>
          <p className="pms-hint">
            Clients ni arrivés ni partis ce jour-là, pris au formulaire depuis l’onglet Départs.
          </p>
          <OtherTakings rows={others} day={date} label="Autres encaissements du jour" />
        </>
      )}
    </section>
  );
}
